import React, { useState, useRef, useEffect } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { ChevronRight, ChevronDown, MoreVertical, Pencil, Trash2, Plus } from '@/ui/icons'
import { WaypointFolderTree } from '@/shared/api/waypointFolders'
import { cn } from '@/shared/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/components'
import { Input } from '@/ui/components'
import { useConfirm } from '@/shared/hooks'
import { ConfirmationDialog } from '@/ui/components'

interface WaypointFolderItemProps {
  folder: WaypointFolderTree
  isExpanded: boolean
  onToggle: () => void
  onSelect?: () => void
  onDelete: () => void
  onRename: (newName: string) => void
  onCreateSubfolder: () => void
  isDraggingOver?: boolean
  depth?: number
  maxDepth?: number
}

export function WaypointFolderItem({
  folder,
  isExpanded,
  onToggle,
  onSelect,
  onDelete,
  onRename,
  onCreateSubfolder,
  isDraggingOver = false,
  depth = 0,
  maxDepth = 3,
}: WaypointFolderItemProps) {
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(folder.name)
  const renameInputRef = useRef<HTMLInputElement>(null)
  const { confirm, confirmState, close, setLoading: setConfirmLoading } = useConfirm()

  const isSystemFolder = folder.is_system

  // Draggable setup (only for non-system folders)
  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `folder-${folder.id}`,
    disabled: isSystemFolder, // System folders cannot be dragged
    data: {
      type: 'folder',
      folder,
    },
  })

  // Droppable setup
  const {
    setNodeRef: setDropRef,
    isOver,
  } = useDroppable({
    id: `drop-folder-${folder.id}`,
    data: {
      type: 'folder',
      folderId: folder.id,
      accepts: ['waypoint', 'folder'],
    },
  })

  // Combined ref for drag and drop
  const setRefs = (node: HTMLDivElement | null) => {
    setDragRef(node)
    setDropRef(node)
  }

  const style = {
    transform: CSS.Translate.toString(transform),
  }

  // Focus input when renaming starts
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus()
      renameInputRef.current.select()
    }
  }, [isRenaming])

  const handleRenameStart = () => {
    setIsRenaming(true)
    setRenameValue(folder.name)
  }

  const handleRenameSubmit = () => {
    if (renameValue.trim() && renameValue !== folder.name) {
      onRename(renameValue.trim())
    }
    setIsRenaming(false)
  }

  const handleRenameCancel = () => {
    setRenameValue(folder.name)
    setIsRenaming(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSubmit()
    } else if (e.key === 'Escape') {
      handleRenameCancel()
    }
  }

  const handleDelete = async () => {
    // Count total subfolders recursively
    const countSubfolders = (folder: WaypointFolderTree): number => {
      let count = folder.children.length
      folder.children.forEach((child) => {
        count += countSubfolders(child)
      })
      return count
    }

    const subfolderCount = countSubfolders(folder)
    const waypointCount = folder.waypoint_count || 0
    const hasSubfolders = subfolderCount > 0
    const hasWaypoints = waypointCount > 0

    // Build dynamic description message
    let description = `Are you sure you want to delete "${folder.name}"?`
    
    if (hasSubfolders || hasWaypoints) {
      description += '\n\nThis will permanently delete:'
      
      if (hasSubfolders) {
        description += `\n- ${subfolderCount} subfolder${subfolderCount === 1 ? '' : 's'} and all their contents`
      }
      
      if (hasWaypoints) {
        description += `\n- ${waypointCount} waypoint${waypointCount === 1 ? '' : 's'} in this folder and its subfolders`
      }
      
      description += '\n\nThis action cannot be undone.'
    } else {
      description += '\n\nThis action cannot be undone.'
    }

    await confirm(
      {
        title: 'Delete Folder',
        description,
        variant: 'destructive',
        confirmText: 'Delete',
      },
      async () => {
        setConfirmLoading(true)
        try {
          await onDelete()
        } finally {
          setConfirmLoading(false)
        }
      }
    )
  }

  const canHaveChildren = depth < maxDepth - 1

  return (
    <>
      <div
        ref={setRefs}
        style={style}
        className={cn(
          'flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors text-foreground',
          isDragging && 'opacity-50',
          isDraggingOver || isOver
            ? 'bg-adventure-100 border-2 border-adventure-300 dark:bg-adventure-900/40 dark:border-adventure-600'
            : cn(
                isSystemFolder && 'bg-gray-50 dark:bg-transparent',
                'hover:bg-gray-50 dark:hover:bg-white/[0.06]'
              )
        )}
      >
        {/* Expand/collapse button */}
        <button
          onClick={onToggle}
          className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-foreground"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* Folder name or rename input */}
        {isRenaming ? (
          <Input
            ref={renameInputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleKeyDown}
            className="h-6 px-2 text-sm flex-1"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="flex-1 truncate cursor-pointer"
            onClick={() => onSelect?.()}
          >
            {folder.name}
            {folder.waypoint_count !== undefined && (
              <span className="ml-2 text-muted-foreground text-xs">
                ({folder.waypoint_count})
              </span>
            )}
          </span>
        )}

        {/* Context menu (only for non-system folders) */}
        {!isSystemFolder && !isRenaming && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-foreground rounded hover:bg-gray-100 dark:hover:bg-white/[0.08]"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleRenameStart}>
                <Pencil className="w-4 h-4 mr-2" />
                Rename
              </DropdownMenuItem>
              {canHaveChildren && (
                <DropdownMenuItem onClick={onCreateSubfolder}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Subfolder
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDelete} className="text-error">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Drag handle (only for non-system folders) */}
        {!isSystemFolder && (
          <div
            {...dragAttributes}
            {...dragListeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-4 h-4" />
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmState?.open ?? false}
        onOpenChange={(open) => !open && close()}
        title={confirmState?.title}
        description={confirmState?.description ?? ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
        onConfirm={confirmState?.onConfirm ?? (() => {})}
        isLoading={confirmState?.isLoading}
      />
    </>
  )
}

